import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: "" };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Log to console + send to backend best-effort
    console.error("[ErrorBoundary]", error, info);
    this.setState({ errorInfo: info.componentStack || "" });
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      if (backend) {
        fetch(`${backend}/api/notifications/client-error`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: error.message,
            stack: error.stack,
            componentStack: info.componentStack,
            url: window.location.href,
            user_agent: navigator.userAgent,
          }),
        }).catch(() => {});
      }
    } catch {
      /* never let logging fail */
    }
  }

  handleReload = () => {
    window.location.href = "/";
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: "" });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground"
          data-testid="error-boundary"
        >
          <div className="max-w-lg w-full bg-card border border-border rounded-xl p-8 shadow-2xl space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-destructive/15 flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h1 className="text-xl font-bold">Une erreur est survenue</h1>
                <p className="text-xs text-muted-foreground">
                  Grafton IA a rencontré un problème et n'a pas pu afficher cette page.
                </p>
              </div>
            </div>

            <div className="bg-muted/30 rounded p-3 text-xs font-mono text-muted-foreground max-h-32 overflow-y-auto">
              {this.state.error?.message || "Erreur inconnue"}
            </div>

            <p className="text-sm text-muted-foreground">
              Nos équipes ont été notifiées. Vous pouvez recharger la page ou revenir à l'accueil.
            </p>

            <div className="flex gap-2">
              <button
                onClick={this.handleReset}
                className="flex-1 bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium hover:bg-primary/90"
                data-testid="error-reload-btn"
              >
                Recharger la page
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 bg-secondary text-secondary-foreground rounded-md px-4 py-2 text-sm font-medium hover:bg-secondary/80"
                data-testid="error-home-btn"
              >
                Retour à l'accueil
              </button>
            </div>

            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Détails techniques</summary>
              <pre className="mt-2 bg-muted/40 p-2 rounded overflow-auto max-h-40 whitespace-pre-wrap">
                {this.state.error?.stack}
                {"\n\n--- Component stack ---\n"}
                {this.state.errorInfo}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
